import React, { useState } from 'react';
import ScheduleSelect from './ScheduleSelect';
import '../../component-styles/ScheduleItemModal.css';
import API_BASE from '../../utils/api.js';
import { withCustomStatuses } from '../../utils/orderStatuses.js';

const DATE_FIELDS = [
  { key: 'targetDate', label: 'Target Date' },
  { key: 'orderedDate', label: 'Ordered Date' },
  { key: 'estimatedDeliveryDate', label: 'Est. Delivery' },
  { key: 'actualDeliveryDate', label: 'Actual Delivery' },
  { key: 'installedDate', label: 'Installed' },
  { key: 'backorderedDate', label: 'Back Order Date' },
];

export default function ScheduleItemModal({
  item,
  typeOptions,
  statusOptions,
  rooms = [],
  schedules = [],
  onScheduleCreated,
  userId,
  projectId,
  onClose,
  onSaved,
}) {
  const isEdit = Boolean(item);

  const [form, setForm] = useState({
    name: item?.name || '',
    scheduleType: item?.scheduleType || 'ffe',
    room: item?.room || '',
    scheduleId: item?.scheduleId || '',
    vendor: item?.vendor || '',
    measurement: item?.measurement || '',
    qty: item?.qty ?? 1,
    unitType: item?.unitType || 'Each',
    leadTime: item?.leadTime || '',
    status: item?.status || statusOptions[0],
    notes: item?.notes || '',
    targetDate: item?.targetDate || '',
    orderedDate: item?.orderedDate || '',
    estimatedDeliveryDate: item?.estimatedDeliveryDate || '',
    actualDeliveryDate: item?.actualDeliveryDate || '',
    installedDate: item?.installedDate || '',
    cfaApproved: item?.cfaApproved || false,
    backorderedDate: item?.backorderedDate || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!form.name.trim()) return setError('Item name is required.');

    setSaving(true);
    setError(null);

    const payload = {
      ...form,
      qty: Number.isFinite(Number(form.qty)) ? Number(form.qty) : 1,
    };

    try {
      const url = isEdit
        ? `${API_BASE}/api/schedule/${encodeURIComponent(userId)}/${encodeURIComponent(item.sk)}`
        : `${API_BASE}/api/schedule/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Request failed');

      const saved = await res.json();
      onSaved(saved);
    } catch (err) {
      console.error('Failed to save schedule item:', err);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="sim-modal-overlay" onClick={onClose}>
      <div className="sim-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="sim-modal__heading">{isEdit ? 'Edit Schedule Item' : 'Add Schedule Item'}</h3>

        <div className="sim-modal__body">
          <div className="sim-modal__field">
            <label className="sim-modal__label">Item Name</label>
            <input
              className="sim-modal__input"
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="e.g., Kohler Kitchen Faucet"
            />
          </div>

          <div className="sim-modal__row">
            <div className="sim-modal__field">
              <label className="sim-modal__label">Type</label>
              <select
                className="sim-modal__input"
                value={form.scheduleType}
                onChange={(e) => setField('scheduleType', e.target.value)}
              >
                {typeOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="sim-modal__field">
              <label className="sim-modal__label">Room</label>
              {rooms.length > 0 ? (
                <select
                  className="sim-modal__input"
                  value={form.room}
                  onChange={(e) => setField('room', e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {rooms.map((r) => (
                    <option key={r.sectionId || r.name} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="sim-modal__input"
                  type="text"
                  value={form.room}
                  onChange={(e) => setField('room', e.target.value)}
                  placeholder="e.g., Kitchen"
                />
              )}
            </div>

            <div className="sim-modal__field">
              <label className="sim-modal__label">Schedule</label>
              <ScheduleSelect
                schedules={schedules}
                value={form.scheduleId}
                onChange={(v) => setField('scheduleId', v)}
                userId={userId}
                projectId={projectId}
                onScheduleCreated={onScheduleCreated}
                selectClassName="sim-modal__input"
              />
            </div>
          </div>

          <div className="sim-modal__row">
            <div className="sim-modal__field">
              <label className="sim-modal__label">Vendor</label>
              <input
                className="sim-modal__input"
                type="text"
                value={form.vendor}
                onChange={(e) => setField('vendor', e.target.value)}
              />
            </div>

            <div className="sim-modal__field">
              <label className="sim-modal__label">Qty</label>
              <input
                className="sim-modal__input"
                type="number"
                min="1"
                value={form.qty}
                onChange={(e) => setField('qty', e.target.value)}
              />
            </div>

            <div className="sim-modal__field">
              <label className="sim-modal__label">Unit</label>
              <input
                className="sim-modal__input"
                type="text"
                value={form.unitType}
                onChange={(e) => setField('unitType', e.target.value)}
                placeholder="e.g., pcs, set, lf"
              />
            </div>
          </div>

          <div className="sim-modal__row">
            <div className="sim-modal__field">
              <label className="sim-modal__label">Measurement</label>
              <input
                className="sim-modal__input"
                type="text"
                value={form.measurement}
                onChange={(e) => setField('measurement', e.target.value)}
                placeholder={'e.g., 24"W x 30"L x 32"H'}
              />
            </div>
          </div>

          <div className="sim-modal__row">
            <div className="sim-modal__field">
              <label className="sim-modal__label">Lead Time</label>
              <input
                className="sim-modal__input"
                type="text"
                value={form.leadTime}
                onChange={(e) => setField('leadTime', e.target.value)}
                placeholder="e.g., 6-8 weeks"
              />
            </div>

            <div className="sim-modal__field">
              <label className="sim-modal__label">Status</label>
              <select
                className="sim-modal__input"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
              >
                {withCustomStatuses(statusOptions, null, form.status).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="sim-modal__field">
              <label className="sim-modal__label">CFA Approved</label>
              <label className="sim-modal__checkbox-row">
                <input
                  type="checkbox"
                  checked={form.cfaApproved}
                  onChange={(e) => setField('cfaApproved', e.target.checked)}
                />
                <span>Approved for procurement</span>
              </label>
            </div>
          </div>

          <div className="sim-modal__section-label">Dates</div>
          <div className="sim-modal__row sim-modal__row--dates">
            {DATE_FIELDS.map(({ key, label }) => (
              <div className="sim-modal__field" key={key}>
                <label className="sim-modal__label">{label}</label>
                <input
                  className="sim-modal__input"
                  type="date"
                  value={form[key]}
                  onChange={(e) => setField(key, e.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="sim-modal__field">
            <label className="sim-modal__label">Notes</label>
            <textarea
              className="sim-modal__textarea"
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              rows={2}
            />
          </div>
        </div>

        {error && <div className="sim-modal__error">{error}</div>}

        <div className="sim-modal__actions">
          <button type="button" className="sim-modal__cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="sim-modal__save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  );
}
